import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { Country } from '@/types/regionTypes';

import { Col } from '@/components/Col';
import { Flex } from '@/components/Flex';
import { AccountRegisterCheckbox } from '@/components/FormFields';
import { TitleField } from '@/components/FormFields/TitleField';
import type { IconProps } from '@/components/Icon';
import { Icon } from '@/components/Icon';
import { InputField } from '@/components/InputField';
import { LegacyButton } from '@/components/LegacyButton';
import { ReCaptcha } from '@/components/ReCaptcha';
import type { SelectOption } from '@/components/Select';
import { SelectField } from '@/components/SelectField';
import { Typography } from '@/components/Typography';
import { useUser } from '@/hooks/useUser';
import { getBrandConfig } from '@/lib/configuration/configuration';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { getPhonePrefixSelectOpts } from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';
import * as M from '@/messages/newsletter';
import { useRegionProvider } from '@/providers/RegionProvider';
import { theme } from '@/theme/getTheme';

import type { CountryOption } from '../SignUpView/helper';
import { SortedNameFields, useExamplePhone } from '../SignUpView/helper';
import {
  getCountrySelectOptions,
  getLocalesByCountryCode,
  getNameFieldOrder,
} from '../SignUpView/helper';
import type { NewsLetterSchema } from './schema';
import { newsLetterSchema } from './schema';

import * as S from './NewsLetterView.styles';

export interface NewsLetterFormProps {
  disabled?: boolean;
  handleSubmit: SubmitHandler<NewsLetterSchema>;
  isProcessing?: boolean;
  source: string;
}

export const NewsLetterForm = ({
  disabled,
  handleSubmit,
  isProcessing = false,
  source = 'page',
}: NewsLetterFormProps) => {
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isVerifyingRecaptcha, setIsVerifyingRecaptchaStatus] = useState(false);
  const [isFormFieldDisabled, setIsFormFieldDisabled] = useState(false);
  const widthLimit = source !== 'modal';

  const {
    snapshot: { user },
  } = useUser();

  const { formatMessage } = useIntl();
  const {
    country,
    locale,
    query: { languageCode },
  } = useRegionProvider();

  const mapCountryToSelectAccepted = (
    country: Country,
    countryOptions?: SelectOption[],
  ) => {
    return (countryOptions?.find((item) => item.value === country.code) ?? {
      label: country.name,
      locales: country.locales,
      name: country.name,
      slug: country.slug,
      value: country.code,
    }) as CountryOption;
  };

  const brandConfig = getBrandConfig();
  const schema = newsLetterSchema(
    formatMessage,
    country,
    locale,
    user,
    isFormFieldDisabled,
    brandConfig.newsletter?.salutationDefaultValue,
  );
  const countryOptions =
    brandConfig.newsletter?.countryOptions ?? getCountrySelectOptions();

  const defaultValues = {
    ...schema.getDefault(),
    country: mapCountryToSelectAccepted(country, countryOptions),
  };
  const formCtx = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });
  const examplePhone = useExamplePhone(formCtx);

  const captcha = formCtx.watch('captcha');
  const currentCountry = formCtx.watch('country');

  useEffect(() => {
    if (user?.id) {
      setIsFormFieldDisabled(true);
      formCtx.reset(defaultValues);
    }
  }, [user?.id]);

  const needPhone = brandConfig.newsletter?.needPhone;
  const languageOptions =
    brandConfig.newsletter?.languageOptions ??
    getLocalesByCountryCode(currentCountry.value);

  const isCountrySelectDisabled =
    isFormFieldDisabled || !brandConfig.newsletter?.countryOptions;

  const isJapan = country.code === 'JP';

  const userTitle = getMetaValue(
    user?.metadata ?? [],
    ACCOUNT_METADATA.personalTitle,
  );

  return (
    <S.FormProvider
      name='sign-up-my-account'
      {...formCtx}
      onSubmit={(data) => {
        handleSubmit({
          ...data,
          reCaptchaResponse: reCaptchaRef.current?.getValue() ?? '',
        });
      }}
    >
      <S.Row $widthLimit={widthLimit} gapDensity='none'>
        {source === 'page' && (
          <Col data-testid='NewsLetterFormPersonalData'>
            <Typography scale='h5' textAlign='center' textTransform='uppercase'>
              {formatMessage(formMessages.personalData)}
            </Typography>
          </Col>
        )}
      </S.Row>
      <S.Row $widthLimit={widthLimit} gapDensity='none'>
        <Col md={6}>
          <TitleField
            colorVariant='grey'
            countryCode={country.code}
            isDisabled={isFormFieldDisabled && !!userTitle}
          />
        </Col>
      </S.Row>
      <S.Row $widthLimit={widthLimit} gapDensity='none'>
        <SortedNameFields
          arr={[
            {
              component: (
                <Col key='firstName' md={source === 'page' ? 12 : 6}>
                  <InputField
                    autoComplete='given-name'
                    className={CONTENTSQUARE_MASKED}
                    colorVariant={source === 'page' ? 'white' : 'grey'}
                    data-testid='NewsLetterFormFirstName'
                    disabled={isFormFieldDisabled}
                    name='firstName'
                    placeholder={formatMessage(
                      isJapan
                        ? formMessages.firstNameForJP
                        : formMessages.firstName,
                    )}
                    required
                    style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
                  />
                </Col>
              ),
              order: getNameFieldOrder(country, locale, 'first'),
            },
            {
              component: (
                <Col key='lastName' md={source === 'page' ? 12 : 6}>
                  <InputField
                    autoComplete='family-name'
                    className={CONTENTSQUARE_MASKED}
                    colorVariant={source === 'page' ? 'white' : 'grey'}
                    data-testid='NewsLetterFormLastName'
                    disabled={isFormFieldDisabled}
                    name='lastName'
                    placeholder={formatMessage(
                      isJapan
                        ? formMessages.lastNameForJP
                        : formMessages.lastName,
                    )}
                    required
                    style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
                  />
                </Col>
              ),
              order: getNameFieldOrder(country, locale, 'last'),
            },
            ...(isJapan
              ? [
                  {
                    component: (
                      <Col key='firstNameKanji' md={source === 'page' ? 12 : 6}>
                        <InputField
                          autoComplete='given-name'
                          className={CONTENTSQUARE_MASKED}
                          colorVariant={source === 'page' ? 'white' : 'grey'}
                          data-testid='NewsLetterFormFirstNameKanji'
                          disabled={isFormFieldDisabled}
                          name='firstNameKanji'
                          placeholder={formatMessage(
                            formMessages.firstNameKanji,
                          )}
                          required
                          style={{
                            borderBottom: '1px solid hsla(0,0%,39%,.3)',
                          }}
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(country, locale, 'firstKanji'),
                  },
                  {
                    component: (
                      <Col key='lastNameKanji' md={source === 'page' ? 12 : 6}>
                        <InputField
                          autoComplete='family-name'
                          className={CONTENTSQUARE_MASKED}
                          colorVariant={source === 'page' ? 'white' : 'grey'}
                          data-testid='NewsLetterFormLastNameKanji'
                          disabled={isFormFieldDisabled}
                          name='lastNameKanji'
                          placeholder={formatMessage(
                            formMessages.lastNameKanji,
                          )}
                          required
                          style={{
                            borderBottom: '1px solid hsla(0,0%,39%,.3)',
                          }}
                        />
                      </Col>
                    ),
                    order: getNameFieldOrder(country, locale, 'lastKanji'),
                  },
                ]
              : []),
          ]}
        />
      </S.Row>
      <S.Row $widthLimit={widthLimit} gapDensity='none'>
        <Col>
          <InputField
            autoComplete='email'
            className={CONTENTSQUARE_MASKED}
            colorVariant={source === 'page' ? 'white' : 'grey'}
            data-testid='NewsLetterFormEmail'
            disabled={isFormFieldDisabled}
            id='inputEmail'
            name='email'
            placeholder={formatMessage(formMessages.email)}
            required
            style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
            type='email'
          />
        </Col>
        {source === 'page' && (
          <Col data-testid='NewsLetterFormLanguageCategory'>
            <Typography scale='h5' textAlign='center' textTransform='uppercase'>
              {formatMessage(formMessages.language)}
            </Typography>
          </Col>
        )}
      </S.Row>
      {needPhone && (
        <S.Row $widthLimit={widthLimit} gapDensity='none'>
          <Col md={3} xs={4}>
            <SelectField
              aria-label={formatMessage(formMessages.phone)}
              colorVariant={source === 'page' ? 'white' : 'grey'}
              data-test-id='SignUpViewPhonePrefixSelectField'
              name='phonePrefix'
              options={getPhonePrefixSelectOpts()}
              phonePrefixDisplayFormat='dialCode'
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderBottom: '1px solid rgba(99, 99, 99, 0.3)',
                }),
              }}
            />
          </Col>
          <Col md={9} xs={8}>
            <InputField
              autoComplete='phone'
              className={CONTENTSQUARE_MASKED}
              colorVariant={source === 'page' ? 'white' : 'grey'}
              data-test-id='SignUpViewPhoneInputField'
              id='SignUpViewPhoneInputField'
              name='phone'
              pattern='[0-9]+'
              placeholder={formatMessage(formMessages.mobile)}
              style={{ borderBottom: '1px solid hsla(0,0%,39%,.3)' }}
              type='text'
            />
            <Typography color='secondaryGrey' scale='p3' textAlign='right'>
              {examplePhone}
            </Typography>
          </Col>
        </S.Row>
      )}

      <S.Row $widthLimit={widthLimit} gapDensity='none'>
        <Col md={source === 'page' ? 12 : 6}>
          <SelectField
            colorVariant={source === 'page' ? 'white' : 'grey'}
            data-testid='NewsLetterFormLanguage'
            isDisabled={isFormFieldDisabled}
            name='language'
            options={languageOptions}
            placeholder={formatMessage(formMessages.language)}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderBottom: '1px solid rgba(99, 99, 99, 0.3)',
              }),
            }}
          />
        </Col>
        <Col md={source === 'page' ? 12 : 6}>
          <SelectField
            colorVariant={source === 'page' ? 'white' : 'grey'}
            isDisabled={isCountrySelectDisabled}
            name='country'
            options={countryOptions}
            placeholder={formatMessage(formMessages.country)}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderBottom: '1px solid rgba(99, 99, 99, 0.3)',
              }),
            }}
          />
        </Col>
        <Col md={12}>
          <Typography color='secondaryGrey' scale='p3' textAlign='right'>
            {formatMessage(formMessages.mandatoryFields)}
          </Typography>
        </Col>
        <S.CheckBoxWrapper data-testid='NewsLetterFormAccountRegisterCheckbox'>
          <AccountRegisterCheckbox
            action={formatMessage(M.defined.newsLetterButton)}
          />
        </S.CheckBoxWrapper>
        <Col>
          <ReCaptcha
            ref={reCaptchaRef}
            data-testid='checkboxCaptcha'
            languageCode={languageCode}
            name='captcha'
            onChange={setIsVerifyingRecaptchaStatus}
            validation={false}
          />
        </Col>
        <Col>
          <Flex justifyContent='center'>
            <LegacyButton
              data-testid='NewsLetterFormButtonSubmit'
              disabled={
                isProcessing || !captcha || isVerifyingRecaptcha || disabled
              }
              isProcessing={isProcessing || isVerifyingRecaptcha}
              label={formatMessage(M.defined.newsLetterButton)}
              leftContent={
                theme?.components?.newsLetter?.button?.icon && (
                  <Icon
                    {...(theme?.components?.newsLetter?.button
                      ?.icon as IconProps)}
                  />
                )
              }
              style={theme?.components?.newsLetter?.button?.style}
              type='submit'
              variant={theme?.components?.newsLetter?.button?.variant}
            ></LegacyButton>
          </Flex>
        </Col>
      </S.Row>
    </S.FormProvider>
  );
};
